import type { ReactNode } from 'react';
import { Badge, Group } from '@mantine/core';
import type { CoachingSubType, ModalityType } from '../../../types';
import { useFeatureFlag } from '../../../stores/featureFlags';
import {
  COACHING_SUB_TYPE_LABELS,
  MODALITY_CONFIG_PILL_LABELS,
} from '../../../config/cohortConfig';

/**
 * MED-1038 (E1.10d): Read-only facilitation-configuration pills rendered on modality
 * headers across the Overview and Sessions tabs. This is the SINGLE reusable component
 * for all four display locations (AC13) so labels and visual language stay identical.
 *
 * Pill rules:
 *  - Group Coaching: sub-type pill always when the enum has a value (AC5, neutral);
 *    Live Delivery pill only when the cohort's Live Session Delivery is on (AC6);
 *    AI pill (Enabled/Off) only when Live Session Delivery is on (AC7). The Live
 *    Delivery + AI pills are additionally gated by the system `live_session_delivery`
 *    feature flag (AC10) — when the flag is off neither renders.
 *    MED-1069 (E1.10c): a "Commitments" pill renders when Commitment Tracking is
 *    enabled on the group coaching modality — flag-gated but INDEPENDENT of Live
 *    Session Delivery, and enabled-only (no pill when off).
 *  - Workshops: AI pill always (Enabled/Off), ungated by the flag (AC8).
 *  - 1:1 Coaching: no pills (AC9) — also the default for any unmatched type.
 *
 * Visual treatment (AC12): a neutral pill for the descriptive sub-type, a filled green
 * pill for an enabled capability (Live Delivery, AI Enabled), and a muted outline pill
 * for "AI Off" — so type vs. on/off state is scannable at a glance.
 *
 * The pills are presentational only (AC11) — configuration is changed solely in the
 * Edit Cohort flow.
 */

/** Minimal config shape needed to render the pills (subset of the Modality record). */
export interface ModalityConfigPillsModality {
  modality_type: ModalityType;
  coaching_sub_type?: CoachingSubType | null;
  ai_features_enabled?: boolean;
  live_session_delivery_enabled?: boolean;
  // MED-1069 (E1.10c): group coaching Commitment Tracking. Drives the
  // "Commitments" pill, shown only when true (and the system flag is on).
  commitment_tracking_enabled?: boolean;
}

interface ModalityConfigPillsProps {
  modality: ModalityConfigPillsModality | null | undefined;
  /** Spacing between pills; defaults to a compact 6px. */
  gap?: number | string;
}

// Shared pill styling — matches the uppercase status-pill language already used in the
// cohort UI (WorkshopStatusBadge, CoachStatusPill): xs / radius sm / 0.3px tracking.
const PILL_STYLE = { letterSpacing: '0.3px' } as const;

/** Descriptive, type-style pill (sub-type). */
function NeutralPill({ children }: { children: ReactNode }) {
  return (
    <Badge size="xs" radius="sm" variant="light" color="gray" tt="uppercase" style={PILL_STYLE}>
      {children}
    </Badge>
  );
}

/** Enabled-capability pill (Live Delivery, AI Enabled). */
function EnabledPill({ children }: { children: ReactNode }) {
  return (
    <Badge size="xs" radius="sm" variant="filled" color="medleyGreen" tt="uppercase" style={PILL_STYLE}>
      {children}
    </Badge>
  );
}

/** Muted/off pill (AI Off). */
function MutedPill({ children }: { children: ReactNode }) {
  return (
    <Badge size="xs" radius="sm" variant="outline" color="gray" tt="uppercase" style={PILL_STYLE}>
      {children}
    </Badge>
  );
}

export function ModalityConfigPills({ modality, gap = 6 }: ModalityConfigPillsProps) {
  const liveSessionDeliveryFlag = useFeatureFlag('live_session_delivery');

  if (!modality) return null;

  const pills: ReactNode[] = [];

  if (modality.modality_type === 'group_coaching') {
    // AC5: sub-type pill always when the enum has a value.
    if (modality.coaching_sub_type) {
      pills.push(
        <NeutralPill key="sub-type">
          {COACHING_SUB_TYPE_LABELS[modality.coaching_sub_type]}
        </NeutralPill>,
      );
    }

    // AC6/AC7/AC10: Live Delivery + AI pills exist only when the cohort has Live
    // Session Delivery on AND the system flag is on.
    if (liveSessionDeliveryFlag && modality.live_session_delivery_enabled) {
      pills.push(
        <EnabledPill key="live-delivery">
          {MODALITY_CONFIG_PILL_LABELS.liveDelivery}
        </EnabledPill>,
      );
      pills.push(
        modality.ai_features_enabled ? (
          <EnabledPill key="ai">{MODALITY_CONFIG_PILL_LABELS.aiEnabled}</EnabledPill>
        ) : (
          <MutedPill key="ai">{MODALITY_CONFIG_PILL_LABELS.aiOff}</MutedPill>
        ),
      );
    }

    // MED-1069 (E1.10c): Commitments pill. Gated by the system flag (AC12) but
    // INDEPENDENT of Live Session Delivery (AC7) — shown whenever Commitment
    // Tracking is enabled, even if Live Delivery is off. Enabled-only: no pill
    // when disabled (AC10), so no muted/off variant.
    if (liveSessionDeliveryFlag && modality.commitment_tracking_enabled) {
      pills.push(
        <EnabledPill key="commitments">{MODALITY_CONFIG_PILL_LABELS.commitments}</EnabledPill>,
      );
    }
  } else if (modality.modality_type === 'workshop') {
    // AC8: Workshop AI pill always renders (ungated by the feature flag).
    pills.push(
      modality.ai_features_enabled ? (
        <EnabledPill key="ai">{MODALITY_CONFIG_PILL_LABELS.aiEnabled}</EnabledPill>
      ) : (
        <MutedPill key="ai">{MODALITY_CONFIG_PILL_LABELS.aiOff}</MutedPill>
      ),
    );
  }
  // AC9: one_on_one (and any other type) → no pills.

  if (pills.length === 0) return null;

  return (
    <Group gap={gap} wrap="wrap">
      {pills}
    </Group>
  );
}

export default ModalityConfigPills;
